/** The command keys, between the town rack and the info plate.
 *
 * The classic's command column is not three lists and a gap: below the racks sits a
 * block of keys - next hero, centre, the hero sheet, the town, the kingdom
 * ledger - and they are the reason the column is worth 13% of the window.
 * Without them the sidebar had a fifth of its height carrying nothing, which is
 * how a panel teaches a player to stop looking at it.
 *
 * Each key fires a binding that already exists rather than owning an action of
 * its own: the screen's hotkey table is the single list of what the adventure
 * map can do, and a button that duplicated a callback would be a second place
 * for that list to drift. A key whose binding is absent (a host that did not
 * supply the overlay hotkeys) is drawn seated and disabled rather than dropped,
 * so the block keeps its shape.
 */

import { useCallback } from "react";
import type { ReactNode } from "react";
import { cx } from "../theme/components.js";
import type { Hotkey } from "../hooks/useHotkeys.js";

export interface CommandKey {
  /** The hotkey combo this key fires. */
  readonly combo: string;
  readonly label: string;
  readonly glyph: ReactNode;
}

/** The shadowed wall of the cut: the mark's own keyline, and its darkest value. */
const RIM = "rgba(26, 15, 2, 0.94)";
/** The floor of the cut - bronze standing in its own shadow, never black. It is
 *  the second value the interior needed: one flat mass has no modelling in it
 *  at any size, and at 24px that is what a silhouette is. */
const BODY = "rgba(96, 68, 22, 0.95)";
/** The lit wall, offset one pixel down from the mark. */
const LIP = "rgba(255, 242, 206, 0.52)";

/**
 * Engraved marks, drawn rather than pulled from the UI atlas: the atlas has no
 * icon for "centre on hero" or "next hero", and a key with a word and no mark
 * is the flat rectangle the brief forbids.
 *
 * The keys are bronze now, so the mark is a true intaglio: the shape is cut
 * DARK into the lit face, and a pale copy sits one pixel below it as the lit
 * lower lip of the cut. It used to be the other way round - a pale gold mass
 * on cold stone - which on bronze would have been a sticker rather than an
 * engraving, and on the lit top third of the face would have had almost no
 * contrast at all.
 */
function Engraved(props: {
  readonly d: string;
  /** Light strokes cut back out of the dark mass: a collar, a gate, a band. */
  readonly detail?: string | undefined;
  readonly outline?: boolean | undefined;
}): JSX.Element {
  const solid = props.outline !== true;
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {/* An engraving is mass first and cut second. A 1px hairline path is
          invisible at 1:1 - a reviewer could not tell the hero key from the
          town key - so the mark is a filled shape, and the features that make
          it recognisable are cut back out of that shape in the metal's own
          light.

          Four passes, and the middle two are what stopped these reading as
          silhouettes. The mark used to be one flat near-black mass with a pale
          copy under it, so the eight keys were two-tone shapes with ragged
          diagonal edges on a fully painted bronze plate, twenty pixels above a
          resource bar carrying modelled gold and gem medallions - two icon
          languages in one column. A cut in metal has a lit wall, a shadowed
          wall and a floor: the light copy below is the lit wall, the RIM is
          the shadowed wall, and the BODY inside it is the floor, which is a
          bronze in shadow rather than a hole. */}
      <g strokeLinecap="round" strokeLinejoin="round">
        <g transform="translate(0,1.15)">
          <path d={props.d} fill={solid ? LIP : "none"} stroke={LIP}
            strokeWidth={solid ? 1.5 : 3.2} />
        </g>
        <path d={props.d} fill={solid ? RIM : "none"} stroke={RIM}
          strokeWidth={solid ? 2.2 : 2.9} />
        <path d={props.d} fill={solid ? BODY : "none"} stroke={BODY}
          strokeWidth={solid ? 0.6 : 1.4} />
        {props.detail !== undefined && (
          <path d={props.detail} fill="none" stroke="rgba(255, 240, 200, 0.72)" strokeWidth="1.7" />
        )}
      </g>
    </svg>
  );
}

/** Chevrons against a stop bar: "go to the next one". */
const NEXT_HERO = "M3 6 l6 6 l-6 6 M11 6 l6 6 l-6 6 M20.5 5 v14";
/** A reticle. */
const CENTRE = "M12 2 v5 M12 17 v5 M2 12 h5 M17 12 h5 M12 8.6 a3.4 3.4 0 1 0 0.01 0";
/** A bust: head and shoulders, with the tunic's V cut into the chest.
 *
 * It was a helm - a dome with a cheek line and a visor slit - and at 24px on a
 * carved key the dome flattened into a mound and the two cheek marks read as a
 * pair of dots. Three reviewers independently could not say what it was. A
 * portrait bust is the mark every strategy game uses for "this hero", it needs
 * no detail below 3px to be recognised, and it matches the portrait socket the
 * key actually opens. */
const HERO_SHEET =
  "M12 2.6 a4.3 4.3 0 1 1 -0.01 0 z M3.4 21.2 c0 -4.8 3.9 -7.3 8.6 -7.3 s8.6 2.5 8.6 7.3 z";
const HERO_DETAIL = "M8.4 15 l3.6 3.6 l3.6 -3.6";
/** A keep: two towers with merlons, a curtain wall and a gate cut into it. */
const TOWN = "M3 20.5 v-12 l2.2 2 l2.2 -2 v-3 h9.2 v3 l2.2 2 l2.2 -2 v12 z";
const TOWN_DETAIL = "M10 20.5 v-5 a2 2 0 0 1 4 0 v5 M7.4 8.5 v12 M16.6 8.5 v12";
/** A crown: three points on a band, with the band cut across it. */
const KINGDOM = "M3 9 l3.6 4.6 l5.4 -7.4 l5.4 7.4 l3.6 -4.6 v9.4 h-18 z";
const KINGDOM_DETAIL = "M3.6 15.6 h16.8";
/** An open book with a ribbon: the journal. */
const JOURNAL = "M12 6.2 C9.6 4.4 6.4 4 3.4 4.4 v13.4 c3 -0.4 6.2 0 8.6 1.8 "
  + "c2.4 -1.8 5.6 -2.2 8.6 -1.8 V4.4 C18.4 4 15.2 4.4 12 6.2 z";
const JOURNAL_DETAIL = "M12 6.6 v12.4";
/** A spade, blade down, with the shoulder cut across it. */
const DIG = "M9.6 3 h4.8 v7.4 h3.6 L12 19 l-6 -8.6 h3.6 z";
const DIG_DETAIL = "M9.6 10.4 h4.8";
/**
 * A jigsaw piece: the puzzle map. A folded map or a scroll would be read as the
 * journal, which is two buttons along - the interlock is the thing that says
 * "pieces", and pieces are what the obelisks take off.
 */
const PUZZLE = "M4 4 h5.6 a1.9 1.9 0 1 1 3.8 0 H19 v5.6 a1.9 1.9 0 1 0 0 3.8 V19 "
  + "h-5.6 a1.9 1.9 0 1 0 -3.8 0 H4 z";
const PUZZLE_DETAIL = "M4 11.5 h6";

/**
 * A lens on a handle, with the map's two directions cut into the glass: a
 * bar for "out" (three quarters, then half), a cross for "in". The keys fire the `-` and `=` combos;
 * `+` answers to `=` as well (see `normalizeKey`), so the pair reads as the
 * minus and plus every map viewer uses.
 */
const LENS = "M10 3 a7 7 0 1 0 0.01 0 M15.2 15.2 l5.8 5.8";
const ZOOM_OUT_DETAIL = "M6.5 10 h7";
const ZOOM_IN_DETAIL = "M6.5 10 h7 M10 6.5 v7";

export const COMMAND_KEYS: readonly CommandKey[] = Object.freeze([
  { combo: "space", label: "Next hero", glyph: <Engraved d={NEXT_HERO} outline /> },
  { combo: "c", label: "Centre", glyph: <Engraved d={CENTRE} outline /> },
  { combo: "h", label: "Hero", glyph: <Engraved d={HERO_SHEET} detail={HERO_DETAIL} /> },
  { combo: "t", label: "Town", glyph: <Engraved d={TOWN} detail={TOWN_DETAIL} /> },
  { combo: "k", label: "Kingdom", glyph: <Engraved d={KINGDOM} detail={KINGDOM_DETAIL} /> },
  { combo: "j", label: "Journal", glyph: <Engraved d={JOURNAL} detail={JOURNAL_DETAIL} /> },
  { combo: "d", label: "Dig", glyph: <Engraved d={DIG} detail={DIG_DETAIL} /> },
  { combo: "p", label: "Puzzle", glyph: <Engraved d={PUZZLE} detail={PUZZLE_DETAIL} /> },
  { combo: "-", label: "Zoom out", glyph: <Engraved d={LENS} detail={ZOOM_OUT_DETAIL} outline /> },
  { combo: "=", label: "Zoom in", glyph: <Engraved d={LENS} detail={ZOOM_IN_DETAIL} outline /> },
]);

export function CommandBar(props: {
  readonly hotkeys: readonly Hotkey[];
  readonly className?: string | undefined;
}): JSX.Element {
  return (
    <div className={cx("hm-adv-keys", props.className)}>
      {COMMAND_KEYS.map((key) => (
        <CommandButton
          key={key.combo}
          spec={key}
          binding={props.hotkeys.find((h) => h.combo === key.combo) ?? null}
        />
      ))}
    </div>
  );
}

function CommandButton(props: {
  readonly spec: CommandKey;
  readonly binding: Hotkey | null;
}): JSX.Element {
  const { binding, spec } = props;
  const fire = useCallback(() => { binding?.onFire(); }, [binding]);
  const off = binding === null || binding.disabled === true;
  return (
    <button
      type="button"
      className="hm-adv-key"
      onClick={fire}
      disabled={off}
      title={`${binding?.description ?? spec.label} (${spec.combo})`}
      aria-label={spec.label}
    >
      {/* Glyph only, as the classic's own command block is: five words at eight
          pixels either truncate or set the key's width, and the name is one
          hover away in the tooltip. */}
      {spec.glyph}
    </button>
  );
}
